import React, { FC, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty, isNull } from 'lodash';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import CustomAccordion from 'containers/LawyerLegalProceedingsPage/CivilProceedingRecap/CustomAccordion';
import { COLORS } from 'constants/design';
import requestRecap from 'images/requestRecap.svg';
import ContestationInfos from 'containers/LawyerLegalProceedingsPage/Contestation/ContestionInfos';
import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerLegalProceedingsPage/messages';
import documentIcon from 'images/document-icon.svg';
import dateIcon from 'images/date-icon.svg';
import ContestationDocuments from 'containers/LawyerLegalProceedingsPage/Contestation/ContestionDocuments';
import ContestationDateAudience from 'containers/LawyerLegalProceedingsPage/Contestation/ContestionDateAudience';
import { Container } from 'components/ToggleTabs/style';
import ContestionDecision from 'containers/LawyerLegalProceedingsPage/Contestation/ContestionDecision';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import { Listings } from 'containers/AppContainer/typings';
import ContestationLawyerOpinion from 'containers/LawyerLegalProceedingsPage/Contestation/ContestationLawyerOpinion';
import DropDownDecisionMenu from 'components/SahamDropDown/DropDownDecisionMenu';
import RejectionDialog from 'containers/LawyerLegalProceedingsPage/Contestation/RejectionDialog';
import { getValueByCode } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import { PROCEDURE_STATUSES } from 'containers/data-structure';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { isDisabled } from 'containers/LawyerLegalProceedingsPage/Contestation/utils';

export const Title = styled.div`
  color: #2b2727;
  font-size: 18px;
  font-weight: bold;
  margin-top: 10px;
  margin-bottom: 10px;
`;

export interface ContestationProps {
  intl: IntlShape;
  judgementOnMerits: any;
  contestationOwners: any;
  documentId: any;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  setDocumentId: Function;
  isLawyer?: boolean;
  listings: Listings;
  updateContestation?: Function | undefined;
  requestUploadContestationDocument?: Function | undefined;
  claimNumber?: string;
  lossDate?: string;
  claim?: any;
  claimId?: number;
  validator?: string;
  isCivilProceeding?: boolean;
  isAssignedLawyer: boolean;
  sagisVictimInfo: any;
  isConsultation: boolean;
  requestGetDocByDocumentumId: Function;
}

const Contestation: FC<ContestationProps> = props => {
  const {
    judgementOnMerits,
    isLawyer,
    listings,
    claimNumber,
    claimId,
    validator,
    isCivilProceeding,
  } = props;

  const [dropDown, setDropDown] = React.useState<null | HTMLElement>(null);

  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState<boolean>(
    false,
  );

  const handleValidation = () => {
    if (props.updateContestation)
      props.updateContestation(
        get(judgementOnMerits, 'jom.jmc.id'),
        get(judgementOnMerits, 'jom.jmc.hearingDate'),
        get(judgementOnMerits, 'jom.jmc.lawyerOpinion'),
        get(judgementOnMerits, 'claimId'),
        get(judgementOnMerits, 'procedureType'),
        get(judgementOnMerits, 'judiciaryProcessId'),
        validator,
        PROCEDURE_STATUSES.VALIDATED,
        get(judgementOnMerits, 'claimNumber'),
        false,
        false,
        handleSuccessCallback,
        null,
      );
  };

  const handleSuccessCallback = () => {
    toast.success(translate(messages.successContestationToastTitle, props));
    setIsRejectionDialogOpen(false);
    if (!featureService.hasFeature(FEATURES.EVOLUTIONS_FEE_NOTE)) {
      pushTo(PAGES.COURT_MANAGER_ASSIGNED_CLAIM)();
    }
  };

  const handleUpdateSuccessCallback = () => {
    toast.success(
      translate(messages.successContestationUpdateToastTitle, props),
    );
  };

  const dropDownMenuElements = [
    {
      title: translate(messages.validateButton, props),
      action: () => handleValidation(),
    },
    {
      title: translate(messages.reject, props),
      action: () => setIsRejectionDialogOpen(true),
    },
  ];

  return (
    <Container style={{ marginTop: 15 }}>
      {get(judgementOnMerits, 'jom.jmc.status', '') ===
        PROCEDURE_STATUSES.REJECTED && (
        <span style={{ marginLeft: '10px', color: COLORS.RED }}>
          {translate(messages.rejectedTitle, props)}
          {` : ${get(judgementOnMerits, 'jom.jmc.rejectionReason')}`}
        </span>
      )}
      <CustomAccordion
        title={translate(messages.contestationInfos, props)}
        textColor={COLORS.ACCORDION_TEXT_BLACK}
        bgColor={COLORS.ACCORDION_BG_YELLOW}
        image={requestRecap}
      >
        <ContestationInfos
          contestation={
            judgementOnMerits &&
            judgementOnMerits.jom &&
            judgementOnMerits.jom.jmc
          }
          updateContestation={props.updateContestation}
          judgementOnMerits={judgementOnMerits}
          isLawyer={isLawyer && props.isAssignedLawyer}
          disabled={
            isDisabled(props.isAssignedLawyer, judgementOnMerits?.jom?.jmc) ||
            props.isConsultation
          }
          listings={props.listings}
          claim={props.claim}
          claimNumber={props.claimNumber}
          sagisVictimInfo={props.sagisVictimInfo}
          isConsultation={props.isConsultation}
        />
      </CustomAccordion>
      {props.isAssignedLawyer ? (
        <CustomAccordion
          title={translate(messages.contestationLawyerOpinion, props)}
          textColor={COLORS.ACCORDION_TEXT_BLACK}
          bgColor={COLORS.ACCORDION_BG_YELLOW}
          image={requestRecap}
        >
          <ContestationLawyerOpinion
            contestationLawyerOpinion={get(
              judgementOnMerits,
              'jom.jmc.lawyerOpinion',
              '',
            )}
            updateContestation={props.updateContestation}
            judgementOnMerits={judgementOnMerits}
            claimNumber={claimNumber}
            isLawyer={isLawyer}
            jmc={get(judgementOnMerits, 'jom.jmc', {})}
            handleSuccessCallback={handleUpdateSuccessCallback}
          />
        </CustomAccordion>
      ) : (
        !isNull(get(judgementOnMerits, 'jom.jmc.lawyerOpinion')) && (
          <CustomAccordion
            title={translate(messages.contestationLawyerOpinion, props)}
            textColor={COLORS.ACCORDION_TEXT_BLACK}
            bgColor={COLORS.ACCORDION_BG_YELLOW}
            image={requestRecap}
          >
            <ContestationLawyerOpinion
              contestationLawyerOpinion={get(
                judgementOnMerits,
                'jom.jmc.lawyerOpinion',
                '',
              )}
              updateContestation={props.updateContestation}
              judgementOnMerits={judgementOnMerits}
              claimNumber={claimNumber}
              isLawyer={isLawyer}
              disabled={true}
              jmc={get(judgementOnMerits, 'jom.jmc', {})}
              handleSuccessCallback={handleUpdateSuccessCallback}
            />
          </CustomAccordion>
        )
      )}
      {props.isAssignedLawyer ? (
        <CustomAccordion
          title={translate(messages.supportingDocuments, props)}
          textColor={COLORS.ACCORDION_TEXT_BLUE}
          bgColor={COLORS.ACCORDION_BG_BLUE}
          image={documentIcon}
        >
          <ContestationDocuments
            uploadDocument={props.uploadDocument}
            uploadedDocument={props.uploadedDocument}
            resetUploadedDocument={props.resetUploadedDocument}
            documentId={props.documentId}
            setDocumentId={props.setDocumentId}
            requestUploadContestationDocument={
              props.requestUploadContestationDocument
            }
            status={judgementOnMerits.status}
            claimNumber={claimNumber}
            contestationId={judgementOnMerits.jom.jmc?.id}
            judgementOnMerits={judgementOnMerits}
            isLawyer={isLawyer && props.isAssignedLawyer}
            handleSuccessCallback={handleUpdateSuccessCallback}
            isCivilProceeding={isCivilProceeding}
            requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
          />
        </CustomAccordion>
      ) : (
        !isEmpty(get(judgementOnMerits, 'jom.jmc.jmcd', [])) && (
          <CustomAccordion
            title={translate(messages.supportingDocuments, props)}
            textColor={COLORS.ACCORDION_TEXT_BLUE}
            bgColor={COLORS.ACCORDION_BG_BLUE}
            image={documentIcon}
          >
            <ContestationDocuments
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadedDocument={props.resetUploadedDocument}
              documentId={props.documentId}
              setDocumentId={props.setDocumentId}
              requestUploadContestationDocument={
                props.requestUploadContestationDocument
              }
              status={judgementOnMerits.status}
              claimNumber={claimNumber}
              contestationId={judgementOnMerits.jom.jmc?.id}
              judgementOnMerits={judgementOnMerits}
              isLawyer={isLawyer && props.isAssignedLawyer}
              handleSuccessCallback={handleUpdateSuccessCallback}
              isCivilProceeding={isCivilProceeding}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          </CustomAccordion>
        )
      )}
      {props.isAssignedLawyer ? (
        <CustomAccordion
          title={translate(messages.contestationAudienceDate, props)}
          textColor={COLORS.ACCORDION_TEXT_BLUE}
          bgColor={COLORS.ACCORDION_BG_BLUE}
          image={dateIcon}
        >
          <ContestationDateAudience
            contestationHearingDate={judgementOnMerits.jom.jmc?.hearingDate}
            updateContestation={props.updateContestation}
            judgementOnMerits={judgementOnMerits}
            claimNumber={claimNumber}
            lossDate={props.lossDate}
            jmc={get(judgementOnMerits, 'jom.jmc', {})}
            isLawyer={isLawyer && props.isAssignedLawyer}
            handleSuccessCallback={handleUpdateSuccessCallback}
          />
        </CustomAccordion>
      ) : (
        !isNull(judgementOnMerits.jom.jmc?.hearingDate) && (
          <CustomAccordion
            title={translate(messages.contestationAudienceDate, props)}
            textColor={COLORS.ACCORDION_TEXT_BLUE}
            bgColor={COLORS.ACCORDION_BG_BLUE}
            image={dateIcon}
          >
            <ContestationDateAudience
              contestationHearingDate={judgementOnMerits.jom.jmc?.hearingDate}
              updateContestation={props.updateContestation}
              judgementOnMerits={judgementOnMerits}
              claimNumber={claimNumber}
              lossDate={props.lossDate}
              jmc={get(judgementOnMerits, 'jom.jmc', {})}
              isLawyer={isLawyer && props.isAssignedLawyer}
              handleSuccessCallback={handleUpdateSuccessCallback}
              disabled={true}
            />
          </CustomAccordion>
        )
      )}
      {props.isAssignedLawyer ? (
        <CustomAccordion
          title={translate(messages.contestationDecision, props)}
          textColor={COLORS.ACCORDION_TEXT_BLUE}
          bgColor={COLORS.ACCORDION_BG_BLUE}
          image={documentIcon}
        >
          <ContestionDecision
            contestationDecisionNatureId={get(
              judgementOnMerits,
              'jom.jmc.jomDecisionNatureId',
            )}
            isLawyer={isLawyer && props.isAssignedLawyer}
            uploadDocument={props.uploadDocument}
            uploadedDocument={props.uploadedDocument}
            resetUploadedDocument={props.resetUploadedDocument}
            documentId={props.documentId}
            setDocumentId={props.setDocumentId}
            listings={listings}
            judgementOnMerits={judgementOnMerits}
            claimNumber={claimNumber}
            contestationId={get(judgementOnMerits, 'jom.jmc.id', '')}
            requestUploadContestationDocument={
              props.requestUploadContestationDocument
            }
            handleSuccessCallback={handleUpdateSuccessCallback}
            isCivilProceeding={isCivilProceeding}
            requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
          />
        </CustomAccordion>
      ) : (
        !isNull(get(judgementOnMerits, 'jom.jmc.jomDecisionNatureId', '')) && (
          <CustomAccordion
            title={translate(messages.contestationDecision, props)}
            textColor={COLORS.ACCORDION_TEXT_BLUE}
            bgColor={COLORS.ACCORDION_BG_BLUE}
            image={documentIcon}
          >
            <ContestionDecision
              contestationDecisionNatureId={get(
                judgementOnMerits,
                'jom.jmc.jomDecisionNatureId',
              )}
              isLawyer={isLawyer && props.isAssignedLawyer}
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadedDocument={props.resetUploadedDocument}
              documentId={props.documentId}
              setDocumentId={props.setDocumentId}
              listings={listings}
              judgementOnMerits={judgementOnMerits}
              claimNumber={claimNumber}
              contestationId={get(judgementOnMerits, 'jom.jmc.id', '')}
              requestUploadContestationDocument={
                props.requestUploadContestationDocument
              }
              handleSuccessCallback={handleUpdateSuccessCallback}
              isCivilProceeding={isCivilProceeding}
              disabled={true}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          </CustomAccordion>
        )
      )}
      {!isLawyer &&
        !props.isConsultation &&
        get(judgementOnMerits, 'jom.jmc.status', '') !==
          PROCEDURE_STATUSES.VALIDATED && (
          <>
            <section className="submit-form">
              <DropDownDecisionMenu
                setDropDown={setDropDown}
                dropDown={dropDown}
                height={'46'}
                label={translate(messages.managerDecisionTitle, props)}
                menuElements={dropDownMenuElements}
              />
            </section>

            <RejectionDialog
              isRejectionDialogOpen={isRejectionDialogOpen}
              setIsRejectionDialogOpen={setIsRejectionDialogOpen}
              updateContestation={props.updateContestation}
              lawyer={getValueByCode(
                get(props.claim, 'claim.assignment[0].assignedLawyer', ''),
                get(listings, 'intervenants', []),
              )}
              claimId={claimId}
              claimNumber={claimNumber}
              jom={judgementOnMerits}
              validator={validator}
            />
          </>
        )}
    </Container>
  );
};

export default injectIntl(Contestation);
